import { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";

const EMPTY = { current: "", next: "", confirm: "" };

const PwField = ({ label, ...props }) => (
  <label className="block">
    <span className="text-sm font-semibold text-foreground">{label}</span>
    <Input type="password" {...props} className="mt-1.5 h-12 rounded-xl border-2 border-border text-base focus-visible:ring-0 focus-visible:border-[color:var(--brand)]" />
  </label>
);

export const ChangePassword = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) { setError("New password must be at least 8 characters"); return; }
    if (form.next !== form.confirm) { setError("New passwords don't match"); return; }
    setSaving(true); setError("");
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password changed");
      setForm(EMPTY); setOpen(false);
    } catch (err) { setError(errorMessage(err)); }
    finally { setSaving(false); }
  };

  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} data-testid="change-password-open-button"
      className="flex h-12 items-center gap-2 rounded-xl border-2 border-border px-4 font-semibold text-foreground">
      <KeyRound className="h-4 w-4" /> Change password
    </button>
  );

  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl bg-muted p-4" data-testid="change-password-form">
      <PwField label="Current password" autoComplete="current-password" required value={form.current} onChange={set("current")} data-testid="current-password-input" />
      <PwField label="New password" autoComplete="new-password" required value={form.next} onChange={set("next")} placeholder="At least 8 characters" data-testid="new-password-input" />
      <PwField label="Confirm new password" autoComplete="new-password" required value={form.confirm} onChange={set("confirm")} data-testid="confirm-password-input" />
      {error && <p className="text-sm text-red-600" data-testid="change-password-error">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={saving} data-testid="change-password-submit-button"
          className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 font-semibold text-white disabled:opacity-50">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Update password
        </button>
        <button type="button" onClick={() => { setOpen(false); setForm(EMPTY); setError(""); }} data-testid="change-password-cancel-button"
          className="h-12 rounded-xl border-2 border-border px-4 font-semibold text-foreground">Cancel</button>
      </div>
    </form>
  );
};
